import { useMemo } from "react";

import shuffle from "lodash/shuffle";

import {
    localStorageData,
    queryParam,
    useRegisterMetadata
} from "../hooks/use-registered-metadata";
import {
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    UserSegmentationTeamType,
    wacToIdentityUserSegMap
} from "../models/user-segmentation";
import {
    TEAM_TYPE_TO_CHECKBOX_MAP,
    checkboxValues
} from "../views/jtbd/constants";

export const getDefaultValueFromTeamType = (
    teamType: UserSegmentationTeamType
) => {
    return TEAM_TYPE_TO_CHECKBOX_MAP[teamType]?.defaultValues;
};

export const mapJTBDtoTemplateChoice = (
    checkboxes: checkboxValues[],
    teamType?: string
) => {
    const checkedBoxes = checkboxes.filter((checkbox) => checkbox.isChecked);

    const teamTypeDefaultValues = teamType
        ? getDefaultValueFromTeamType(teamType as UserSegmentationTeamType)
        : undefined;

    if (checkedBoxes.length === 0 && teamTypeDefaultValues) {
        return teamTypeDefaultValues;
    }
    const sortedCheckboxes = checkedBoxes.sort(
        (a, b) => a.priority - b.priority
    );

    return {
        template: sortedCheckboxes[0]?.template,
        persona: sortedCheckboxes[0]?.persona
    };
};

/**
 * Returns current URL with query parameters added
 *
 * @param params {object} - query parameters to add
 *
 * @returns {string} - URL with query parameters
 */
export function getCurrentUrlWithParams(params: { [key: string]: string }) {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([param, value]) => {
        url.searchParams.set(param, value);
    });
    return url.toString();
}

export const getJtbdTemplateOverrideQueryParam = () => {
    return (
        new URLSearchParams(window.location.search).get(
            "jtbdTemplateOverride"
        ) ?? undefined
    );
};

export const ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN: UserSegmentationTeamType[] = [
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
];

export const useEligibleTeamTypeForJtbdLocalStorage = () => {
    const metadata = useRegisterMetadata();
    const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
        (teamType) =>
            metadata?.get(localStorageData.team) ===
            wacToIdentityUserSegMap[teamType]
    );

    return foundTeamType;
};

export const useGetTeamTypeData = () => {
    const metadata = useRegisterMetadata();

    const teamType = useMemo(() => {
        // Reconcile team between query params and local storage
        const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
            (teamType) =>
                metadata?.get(queryParam.teamType) === teamType ||
                metadata?.get(localStorageData.team) ===
                    wacToIdentityUserSegMap[teamType]
        );

        return foundTeamType ?? undefined;
    }, [metadata]);

    const isSoftwareTeamType =
        teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;

    if (isSoftwareTeamType) {
        const newTeamTypeCheckboxes = shuffle(
            TEAM_TYPE_TO_CHECKBOX_MAP[teamType]?.checkboxValues
        );
        return {
            teamType,
            teamTypeCheckboxes: newTeamTypeCheckboxes,
            isSoftwareTeamType
        };
    }

    const teamTypeCheckboxes = teamType
        ? TEAM_TYPE_TO_CHECKBOX_MAP[teamType]?.checkboxValues
        : undefined;

    return { teamType, teamTypeCheckboxes };
};

export const isEligibleTeamTypeToViewJtbdScreen = (
    teamType: UserSegmentationTeamType
) => {
    return ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.includes(teamType);
};
